'use client';

import { useCallback, useMemo, useState } from 'react';

import { useAuiState } from '@assistant-ui/react';
import { useLocale } from 'next-intl';

import { useConfigValue } from '@/components/providers/config-provider';
import type { TargetDomainEntry } from '@/lib/types/tenant';
import { hasLocalizedTargetDomains } from '@/lib/utils/host-locale';
import { detectTextLanguage } from '@/lib/utils/language-detection';
import { hasManualLocaleChoice } from '@/lib/utils/locale';
import { buildAvailableLocaleSet, extractBaseLocale } from '@/lib/utils/locale-shared';

export interface LanguageSwitchState {
  detectedLocale: string;
  currentLocale: string;
}

export function useMessageLanguageDetection() {
  const locale = useLocale();
  const hasUserMessages = useAuiState(({ thread }) => thread.messages.some(m => m.role === 'user'));
  const explicit = useConfigValue<boolean | undefined>('meta.enableMessageLanguageDetection');
  const targetDomains = useConfigValue<readonly TargetDomainEntry[]>(
    'navigation.iframeNavigation.targetDomains',
  );
  const enabled = explicit ?? hasLocalizedTargetDomains(targetDomains);
  const availableLocales = useConfigValue<readonly string[]>('meta.availableLocales');
  const [languageSwitchState, setLanguageSwitchState] = useState<LanguageSwitchState | null>(null);
  const availableLocalesSet = useMemo(
    () => buildAvailableLocaleSet(availableLocales),
    [availableLocales],
  );

  const checkAndPromptLanguageSwitch = useCallback(
    (text: string): boolean => {
      if (!enabled) return false;
      if (hasManualLocaleChoice()) return false;
      if (hasUserMessages) return false;

      const detected = detectTextLanguage(text, locale);
      if (!detected) return false;
      // Compare base languages so `he` vs `he-IL` (or `iw` vs `he`) don't
      // trigger a switch-to-the-same-language prompt.
      if (extractBaseLocale(detected) === extractBaseLocale(locale)) return false;
      if (!availableLocalesSet.has(detected)) return false;

      setLanguageSwitchState({ detectedLocale: detected, currentLocale: locale });
      return true;
    },
    [enabled, locale, availableLocalesSet, hasUserMessages],
  );

  const clearLanguageSwitchState = useCallback(() => {
    setLanguageSwitchState(null);
  }, []);

  return { checkAndPromptLanguageSwitch, languageSwitchState, clearLanguageSwitchState };
}
